import { effectiveService, isConnectedService, isTimeFrameParam } from '@dawn/feature-config';
import { useNavigate } from 'react-router-dom';

import { GLOBAL_FILTERS } from '@/config/filters.config';
import {
  serializeAccessDiscoveryLink,
  type AccessDiscoveryLink,
} from '@/lib/access-legitimacy-scope';
import { useConnectedServicesStore } from '@/stores/connected-services';
import { useDashboardFilterStore } from '@/stores/dashboard-filter';
import { useWidgetFilterStore } from '@/stores/widget-filter';

// What the Overview shows when the store holds no time range (GlobalFilterRow
// falls back to the same default), e.g. for an admin who never picked one.
const TIME_FRAME_DEFAULT = GLOBAL_FILTERS.find((filter) => filter.id === 'timeFrame')?.defaultValue;

/**
 * Drill from an Overview widget into the events grid (TDEV-1114). The link
 * carries the Overview time range and this widget's service filter, so the grid
 * opens on the window and service the widget was drawn for. Widgets never read
 * the stores to build a link themselves.
 */
export function useDrillToAccessDiscovery(widgetId: string) {
  const navigate = useNavigate();
  const timeFrame = useDashboardFilterStore((s) => s.filters.timeFrame ?? TIME_FRAME_DEFAULT);
  const stored = useWidgetFilterStore((s) => s.filters[widgetId]?.service);
  const service = effectiveService(
    stored,
    useConnectedServicesStore((s) => s.services)
  );
  return (link: Omit<AccessDiscoveryLink, 'timeFrame' | 'service'>) => {
    const params = serializeAccessDiscoveryLink({
      ...link,
      ...(isTimeFrameParam(timeFrame) ? { timeFrame } : {}),
      ...(isConnectedService(service) ? { service } : {}),
    });
    navigate(`/access-discovery?${params.toString()}`);
  };
}
